import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  HeartPulse,
  Home as HomeIcon,
  ShieldAlert,
  Compass,
  GraduationCap,
  Layers,
  PhoneCall,
  Clock,
  ShieldCheck,
  LayoutDashboard,
  Sparkles,
  Building2,
} from "lucide-react";
import { SERVICES } from "../../data/publicSite";
import usePageMeta from "../../lib/usePageMeta";

const ICON_MAP = {
  Users,
  HeartPulse,
  Home: HomeIcon,
  ShieldAlert,
  Compass,
  GraduationCap,
};

const CORE_SLUGS = ["workplace-chaplaincy", "crisis-grief-bereavement"];
const CARE_SETTINGS_SLUGS = ["healthcare-hospice", "senior-living"];

const INDUSTRY_CARDS = [
  {
    slug: "automotive-dealerships",
    label: "Automotive & Dealerships",
    copy: "Confidential chaplain support for sales teams, service departments, technicians, operations staff, and dealership leadership.",
  },
  {
    slug: "restaurants-hospitality",
    label: "Restaurants, Fast-Food Franchises & Hospitality",
    copy: "Accessible chaplain care for fast-paced, people-centered teams across restaurants, quick-service and fast-food franchises, hotels, guest services, and hospitality operations.",
  },
  {
    slug: "construction-skilled-trades",
    label: "Construction & Skilled Trades",
    copy: "Relationship-based support for field crews, skilled trades, supervisors, project teams, and leadership.",
  },
  {
    slug: "real-estate-brokerages-title-services",
    label: "Real Estate, Brokerages & Title Services",
    copy: "Confidential support for real estate agents, brokers, brokerage office teams, title & escrow professionals, and organizational leadership.",
  },
];

const BASELINE_ITEMS = [
  { icon: Clock, label: "24/7 On-Call Chaplain Access" },
  { icon: PhoneCall, label: "CALLACHAP" },
  { icon: LayoutDashboard, label: "CCCHAPS Care Portal" },
  { icon: Sparkles, label: "CCCHAPS On Demand" },
];

function servicesBySlugs(slugs) {
  return slugs.map((slug) => SERVICES.find((s) => s.slug === slug)).filter(Boolean);
}

function ServiceTile({ s }) {
  const Icon = ICON_MAP[s.icon] || Users;
  const title = s.cardTitle || s.title;
  const summary = s.cardSummary || s.summary;
  return (
    <Link
      to={`/services/${s.slug}`}
      data-testid={`services-index-tile-${s.slug}`}
      className="group bg-white border border-slate-200 rounded-2xl p-7 hover:border-[#4A6FA5] hover:shadow-md transition-all flex flex-col"
    >
      <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
        <Icon className="w-5 h-5" strokeWidth={1.75} />
      </span>
      <h3 className="mt-5 font-heading text-xl font-medium text-slate-900">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{summary}</p>
      <div className="mt-5 text-xs uppercase tracking-[0.18em] text-[#4A6FA5] font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
        Learn more <ArrowRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}

export default function ServicesIndex() {
  usePageMeta({
    title: "Services | California Corporate Chaplains",
    description:
      "Workplace chaplaincy, healthcare & hospice support, senior living, crisis & grief care, and industry-specific chaplain support — from California Corporate Chaplains.",
  });

  const coreServices = servicesBySlugs(CORE_SLUGS);
  const careSettingsServices = servicesBySlugs(CARE_SETTINGS_SLUGS);

  return (
    <div
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20"
      data-testid="services-index"
    >
      {/* HERO */}
      <div className="max-w-2xl mb-14">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
          Services
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-slate-900">
          A full spectrum of chaplaincy support.
        </h1>
        <p className="mt-4 text-lg text-slate-600 leading-relaxed">
          Relationship-based, non-clinical care designed to fit into the flow
          of work — flexible, confidential, and human.
        </p>
      </div>

      {/* CORE CHAPLAINCY SERVICES */}
      <section data-testid="services-index-core-section" className="mb-16 md:mb-20">
        <div className="mb-8 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
            Core Chaplaincy Services
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
            Relationship-based chaplain care for the everyday and the hardest days.
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Confidential, non-clinical support for employees, leaders, and
            teams — from steady workplace chaplaincy to on-call presence
            through crisis, grief, and bereavement.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coreServices.map((s) => (
            <ServiceTile key={s.slug} s={s} />
          ))}
        </div>
      </section>

      {/* HEALTHCARE & CARE SETTINGS */}
      <section data-testid="services-index-care-settings-section" className="mb-16 md:mb-20">
        <div className="mb-8 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
            Healthcare &amp; Care Settings
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
            Chaplain care for the people who care for others.
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Non-clinical chaplain support shaped around the pace, pressures,
            and populations of healthcare, hospice, and senior-care
            environments — for staff, patients, residents, and families,
            within appropriate chaplain scope.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {careSettingsServices.map((s) => (
            <ServiceTile key={s.slug} s={s} />
          ))}
        </div>
      </section>

      {/* INDUSTRIES WE SERVE */}
      <section id="industries-we-serve" data-testid="services-index-industries-section" className="mb-16 md:mb-20 scroll-mt-24">
        <div className="mb-8 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
            Industries We Serve
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
            Chaplain care shaped around the realities of your workplace.
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            From dealerships and restaurants to construction teams, healthcare
            organizations, and senior care communities, CCCHAPS adapts
            confidential, relationship-based chaplain support to the people,
            schedules, pressures, and culture of each organization.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {INDUSTRY_CARDS.map((i) => (
            <Link
              key={i.slug}
              to={`/industries/${i.slug}`}
              data-testid={`services-index-industry-tile-${i.slug}`}
              className="group bg-white border border-slate-200 rounded-2xl p-7 hover:border-[#4A6FA5] hover:shadow-md transition-all flex flex-col"
            >
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                <Building2 className="w-5 h-5" strokeWidth={1.75} />
              </span>
              <h3 className="mt-5 font-heading text-xl font-medium text-slate-900">{i.label}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{i.copy}</p>
              <div className="mt-5 text-xs uppercase tracking-[0.18em] text-[#4A6FA5] font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                Learn more <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WAYS TO BRING CCCHAPS TO YOUR ORGANIZATION */}
      <section
        data-testid="services-index-ways-section"
        className="mb-14 md:mb-16"
      >
        <div className="mb-8 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
            Access model
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
            Ways to Bring CCCHAPS to Your Organization
          </h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Organizations can build a broader Care Team Package or provide
            focused on-call chaplain access through CALLACHAP. Both approaches
            are designed around confidential, relationship-based care and the
            needs of the workforce being served.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* CARE TEAM PACKAGES */}
          <article
            data-testid="services-index-care-team-packages"
            className="rounded-2xl border border-slate-200 bg-white p-7 flex flex-col"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
              <Layers className="w-5 h-5" strokeWidth={1.75} />
            </span>
            <h3 className="mt-5 font-heading text-xl font-medium text-slate-900">
              Care Team Packages
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
              Build a chaplain care arrangement around your workforce,
              locations, desired on-site presence, and organizational needs.
              Care Team Packages can combine scheduled on-site and remote
              support while maintaining 24/7 on-call chaplain access.
            </p>
            <Link
              to="/packages"
              data-testid="services-index-care-team-packages-cta"
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#4A6FA5] hover:text-[#3B597B]"
            >
              Explore Care Team Packages <ArrowRight className="w-4 h-4" />
            </Link>
          </article>

          {/* CALLACHAP */}
          <article
            data-testid="services-index-callachap"
            className="rounded-2xl border border-slate-200 bg-white p-7 flex flex-col"
          >
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
              <PhoneCall className="w-5 h-5" strokeWidth={1.75} />
            </span>
            <h3 className="mt-5 font-heading text-xl font-medium text-slate-900">
              CALLACHAP
            </h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
              Give employees confidential access to chaplain support through
              configured phone, text, confidential-message, and available
              remote-support channels — as part of a broader Care Team
              Package or as an on-call-focused organizational arrangement.
            </p>
            <Link
              to="/callachap"
              data-testid="services-index-callachap-cta"
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#4A6FA5] hover:text-[#3B597B]"
            >
              Explore CALLACHAP <ArrowRight className="w-4 h-4" />
            </Link>
          </article>
        </div>
      </section>

      {/* INCLUDED ACCESS & SUPPORT */}
      <section
        data-testid="services-index-included-band"
        className="rounded-2xl border border-blue-100 bg-[#F0F4F8] p-6 md:p-8"
      >
        <div className="max-w-3xl mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-2">
            Included Access &amp; Support
          </p>
          <p className="text-slate-700 leading-relaxed">
            Every CCCHAPS service arrangement includes 24/7 on-call chaplain
            access and the digital support tools available through the
            organization's care arrangement.
          </p>
        </div>

        <ul
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
          data-testid="services-index-baseline-items"
        >
          {BASELINE_ITEMS.map((it) => {
            const Icon = it.icon;
            return (
              <li
                key={it.label}
                className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3"
              >
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                  <Icon className="w-4 h-4" strokeWidth={1.75} />
                </span>
                <span className="text-sm font-medium text-slate-800 leading-tight">
                  {it.label}
                </span>
              </li>
            );
          })}
        </ul>

        <div className="mt-6 flex items-start gap-3 text-xs text-slate-600 leading-relaxed">
          <ShieldCheck className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
          <p>
            Response times vary by channel. Individual chaplain conversations
            are confidential within appropriate professional, ethical,
            safety, and legal limits.
          </p>
        </div>
      </section>
    </div>
  );
}
